import {
  ErrPayloadSchema,
  MatchEndPayloadSchema,
  MatchEventPayloadSchema,
  MatchPausePayloadSchema,
  MatchStartPayloadSchema,
  channelName,
  type MatchEndPayload,
  type MatchEventPayload,
  type MatchPausePayload,
  type MatchSidePayload,
  type MatchStartPayload,
  type SubWindow,
} from "@legacy-manager/shared";
import { FORMATIONS } from "@legacy-manager/engine";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { LivePlayerRow } from "../game/types";
import { fatorDaPartida, useT } from "../i18n";
import type { ClubeDaVisao, JogadorDoMeuClube } from "../online/esquemas";
import { useRelogio, useSessao } from "../online/sessao";
import { Bar } from "../ui/atoms";
import { Partida, type Situacao } from "./partida/Partida";
import type { TrocaDaPartida } from "./partida/SubstituicaoNoCampo";
import { especieDoLance, jogadaDe, type LadoDaPartida, type LanceDaPartida, type PartidaView } from "./partida/tipos";

/**
 * Partida ao vivo transmitida pelo servidor, na mesma página inteira do solo.
 *
 * Por dentro é o contrário do solo: lá o motor roda no aparelho e a tela pede
 * o próximo pedaço quando quer; aqui o servidor já sabe o resultado inteiro e
 * **segura a entrega**, mandando os lances em ritmo controlado pelo canal
 * `match:{id}` (docs/WEBSOCKET.md 4). Cada mensagem traz um trecho de cinco
 * minutos, e o campo desenha o trecho no tempo que o servidor dá até o
 * próximo.
 *
 * Isso muda duas coisas na interface, e as duas são de propósito:
 *
 * Não existe botão de ritmo nem de pular. O jogo é o mesmo para todo mundo que
 * está assistindo, e adiantar para um adiantaria para ninguém.
 *
 * A substituição não é uma troca local, é uma decisão enviada. Ela vale dentro
 * da janela de pausa, o servidor confirma ou recusa, a simulação só segue
 * depois, e a troca entra no resultado oficial da rodada como qualquer outra
 * decisão. Por isso a tela mostra o que foi mandado, e não o que ela gostaria
 * que tivesse acontecido.
 */

interface Lance extends MatchEventPayload {
  /** Ordem de chegada, porque dois lances podem cair no mesmo minuto. */
  ordem: number;
}

/** Quanto tempo real o campo tem para desenhar cada trecho, para acompanhar a transmissão. */
const ORCAMENTO_DO_TRECHO_MS = 4500;

const MAX_TROCAS = 5;

export function PartidaAoVivo({
  matchId,
  clubes,
  meuClubeId,
  elenco,
  aoFechar,
}: {
  matchId: string;
  /** Clubes da liga, da visão filtrada. É o que dá escudo e cor aos dois lados. */
  clubes: ClubeDaVisao[];
  meuClubeId: string | null;
  /** Meu elenco, para a substituição saber força e condição de quem entra e sai. */
  elenco: JogadorDoMeuClube[];
  aoFechar: () => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();

  const [inicio, definirInicio] = useState<MatchStartPayload | null>(null);
  const [lances, definirLances] = useState<Lance[]>([]);
  const [pausa, definirPausa] = useState<(MatchPausePayload & { em: number }) | null>(null);
  const [fim, definirFim] = useState<MatchEndPayload | null>(null);
  const [trocas, definirTrocas] = useState<Array<{ sai: string; entra: string }>>([]);
  /** O ajuste tático pedido no intervalo, aplicado na tela enquanto o servidor confirma. */
  const [ajuste, definirAjuste] = useState<{ formation: string; starters: string[] } | null>(null);
  const [recusa, definirRecusa] = useState<string | null>(null);
  const [trocando, definirTrocando] = useState(false);

  const canal = useMemo(() => channelName("match", matchId), [matchId]);
  const agora = useRelogio(pausa !== null);

  useEffect(() => {
    sessao.socket.assinar(canal);
    let contador = 0;

    const parar = sessao.socket.ouvir((tipo, carga, deQualCanal) => {
      // `err` chega sem canal, porque é resposta a uma mensagem e não evento de
      // um canal. É assim que a recusa de uma substituição aparece.
      if (tipo === "err" && deQualCanal === null) {
        const lido = ErrPayloadSchema.safeParse(carga);
        if (lido.success) definirRecusa(lido.data.message);
        return;
      }
      if (deQualCanal !== canal) return;

      switch (tipo) {
        case "match.start": {
          const lido = MatchStartPayloadSchema.safeParse(carga);
          if (lido.success) definirInicio(lido.data);
          return;
        }
        case "match.event": {
          const lido = MatchEventPayloadSchema.safeParse(carga);
          if (!lido.success) return;
          const ordem = contador;
          contador += 1;
          definirLances((atuais) => [...atuais, { ...lido.data, ordem }]);
          return;
        }
        case "match.pause": {
          const lido = MatchPausePayloadSchema.safeParse(carga);
          if (lido.success) definirPausa({ ...lido.data, em: Date.now() });
          return;
        }
        case "match.end": {
          const lido = MatchEndPayloadSchema.safeParse(carga);
          if (lido.success) definirFim(lido.data);
          definirPausa(null);
          return;
        }
        default:
          return;
      }
    });

    return () => {
      parar();
      sessao.socket.desassinar(canal);
    };
  }, [sessao, canal]);

  // A janela fecha sozinha quando o prazo vence: o servidor segue o jogo com ou
  // sem decisão, e deixar a lista aberta só enganaria quem está escolhendo.
  useEffect(() => {
    if (!pausa) return;
    const id = window.setTimeout(() => {
      definirPausa(null);
    }, pausa.deadlineMs);
    return () => window.clearTimeout(id);
  }, [pausa]);

  const casa = clubes.find((clube) => clube.name === inicio?.home) ?? null;
  const fora = clubes.find((clube) => clube.name === inicio?.away) ?? null;
  const meuLado =
    meuClubeId && casa?.id === meuClubeId ? "home" : meuClubeId && fora?.id === meuClubeId ? "away" : null;

  // A pausa abre a substituição para quem tem clube na partida; o prazo a fecha.
  useEffect(() => {
    if (pausa && meuLado) definirTrocando(true);
    if (!pausa) definirTrocando(false);
  }, [pausa, meuLado]);

  const partida = useMemo<PartidaView | null>(() => {
    if (!inicio || !casa || !fora) return null;
    const lados = {
      home: ladoDaPartida(casa, inicio.sides?.home ?? null),
      away: ladoDaPartida(fora, inicio.sides?.away ?? null),
    };
    const todos = lances.map((lance): LanceDaPartida => ({
      ordem: lance.ordem,
      minute: lance.minute,
      kind: especieDoLance(lance.kind, lance.key),
      clubId: lance.clubId ?? null,
      playerId: lance.playerId ?? null,
      assistId: lance.assistId ?? null,
      shooterId: lance.shooterId ?? null,
      play: jogadaDe(lance.play),
      key: lance.key,
      params: lance.params,
    }));
    // Quem entrou e quem foi expulso, lido dos lances: o campo mostra o time como está.
    for (const lance of todos) {
      const lado = lance.clubId === casa.id ? lados.home : lance.clubId === fora.id ? lados.away : null;
      if (!lado) continue;
      if (lance.kind === "substituicao" && lance.playerId && lance.assistId) {
        const vaga = lado.emCampo.find((v) => v.jogador.id === lance.assistId);
        const entra = lado.todos.get(lance.playerId);
        if (vaga && entra) vaga.jogador = entra;
      }
      if (lance.kind === "vermelho" && lance.playerId) {
        const vaga = lado.emCampo.find((v) => v.jogador.id === lance.playerId);
        if (vaga) vaga.sentOff = true;
      }
    }
    // O ajuste do intervalo no meu lado: a formação nova e a ordem das vagas.
    if (ajuste && meuLado) {
      const lado = lados[meuLado];
      const formacao = FORMATIONS[ajuste.formation];
      const porId = new Map(lado.emCampo.map((vaga) => [vaga.jogador.id, vaga]));
      if (formacao && ajuste.starters.every((id) => porId.has(id))) {
        lado.formation = ajuste.formation;
        lado.slots = [...formacao.slots];
        lado.custom = null;
        lado.emCampo = [
          ...ajuste.starters.map((id) => porId.get(id) as (typeof lado.emCampo)[number]),
          ...lado.emCampo.filter((vaga) => !ajuste.starters.includes(vaga.jogador.id)),
        ];
      }
    }
    const golsDe = (clubId: string): number =>
      todos.filter((lance) => lance.kind === "gol" && lance.playerId && lance.clubId === clubId).length;
    return {
      competition: "",
      stage: "",
      home: lados.home,
      away: lados.away,
      homeGoals: fim ? fim.score[0] : golsDe(casa.id),
      awayGoals: fim ? fim.score[1] : golsDe(fora.id),
      minute: todos.reduce((m, lance) => (lance.minute < 91 ? Math.max(m, lance.minute) : m), 0),
      finished: fim !== null,
      myClubId: meuLado ? (meuLado === "home" ? casa.id : fora.id) : null,
      lances: todos,
      placares: [],
    };
  }, [inicio, lances, fim, casa, fora, meuLado, ajuste]);

  const mandarTroca = (sai: string, entra: string, janela: SubWindow): void => {
    definirRecusa(null);
    sessao.socket.enviar("match.sub", { matchId, out: sai, in: entra, window: janela });
    definirTrocas((atuais) => [...atuais, { sai, entra }]);
  };

  const mandarAjuste = (formation: string, starters: string[]): void => {
    definirRecusa(null);
    sessao.socket.enviar("match.shape", { matchId, formation, starters, window: 2 });
    definirAjuste({ formation, starters });
  };

  const restanteDaPausa = pausa ? Math.max(0, pausa.em + pausa.deadlineMs - agora) : 0;
  const rodape = useRef<HTMLDivElement | null>(null);

  if (!partida) {
    return (
      <div className="pf-online">
        <div className="card" style={{ padding: 18, display: "grid", gap: 12, justifyItems: "start" }}>
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            {t("partidaOnline.conectando")}
          </p>
          <button type="button" className="ghost" onClick={aoFechar}>
            {t("partidaOnline.fechar")}
          </button>
        </div>
      </div>
    );
  }

  // A segunda janela de substituição é a dos 45 minutos: o intervalo, com a
  // troca de lado. As outras são pausas com o jogo do jeito que estava.
  const situacao: Situacao = fim ? "fim" : pausa ? (pausa.window === 2 ? "intervalo" : "pausado") : "jogando";
  const meu = meuLado ? partida[meuLado] : null;

  // O meu time para a troca: os que estão em campo e o banco, com força e condição do elenco.
  const porId = new Map(elenco.map((jogador) => [jogador.id, jogador]));
  const linha = (id: string, slot: string): LivePlayerRow => {
    const jogador = porId.get(id);
    return {
      id,
      name: jogador?.name ?? id,
      pos: jogador?.pos ?? slot,
      slot,
      strength: jogador?.strength ?? 0,
      condition: Math.round(jogador?.condition ?? 100),
      shirt: jogador?.shirt ?? null,
      nationality: "",
    };
  };
  const lado = meuLado && inicio ? inicio.sides?.[meuLado] ?? null : null;
  const enviados = new Set(trocas.flatMap((troca) => [troca.sai, troca.entra]));
  // As trocas pedidas nesta janela já aparecem em campo: quem entrou está na
  // vaga de quem saiu, e é com esses que o ajuste tático é montado.
  const entrouPor = new Map(trocas.map((troca) => [troca.sai, troca.entra]));
  const emCampo: LivePlayerRow[] = meu
    ? meu.emCampo
        .filter((vaga) => !vaga.sentOff)
        .map((vaga, i) => linha(entrouPor.get(vaga.jogador.id) ?? vaga.jogador.id, meu.slots[i] ?? vaga.jogador.pos))
    : [];
  const noCampoOuEnviados = new Set([...(meu?.emCampo.map((v) => v.jogador.id) ?? []), ...enviados]);
  const banco: LivePlayerRow[] = lado
    ? lado.players.filter((jogador) => !noCampoOuEnviados.has(jogador.id)).map((jogador) => linha(jogador.id, jogador.pos))
    : [];
  const trocasFeitas =
    partida.lances.filter((lance) => lance.kind === "substituicao" && lance.clubId === partida.myClubId).length + trocas.length;

  const troca: TrocaDaPartida | null = meu
    ? {
        lado: meu,
        emCampo,
        banco,
        trocasRestantes: Math.max(0, MAX_TROCAS - trocasFeitas),
        bloqueado: !pausa,
        rotulo: pausa
          ? `${t("partidaOnline.janela", { janela: pausa.window })} · ${t("partidaOnline.tempoParaDecidir", { segundos: Math.ceil(restanteDaPausa / 1000) })}`
          : undefined,
        aoTrocar: (sai, entra) => {
          if (pausa) mandarTroca(sai, entra, pausa.window);
        },
        // Só na janela dos 45 minutos o treinador redesenha o time.
        ...(situacao === "intervalo" ? { ajuste: { aoAjustar: mandarAjuste } } : {}),
        // No intervalo não há para onde voltar: o campo fica parado até o
        // servidor apitar o segundo tempo, e a janela fecha sozinha.
        acoes:
          situacao === "intervalo" ? null : (
            <button type="button" className="ghost" onClick={() => definirTrocando(false)}>
              {t("partida.voltar")}
            </button>
          ),
      }
    : null;

  const controles = (
    <>
      {pausa && meu && !trocando && (
        <button type="button" className="ghost" onClick={() => definirTrocando(true)}>
          {t("partida.substituicao")}
        </button>
      )}
      {/* No fim, o "Fechar" é o do meio do campo, um só. */}
      {!fim && (
        <button type="button" className="ghost" onClick={aoFechar}>
          {t("partidaOnline.fechar")}
        </button>
      )}
    </>
  );

  return (
    <div className="pf-online" ref={rodape}>
      <Partida
        partida={partida}
        velocidade={1}
        orcamentoMs={ORCAMENTO_DO_TRECHO_MS}
        parado={pausa !== null || fim !== null}
        situacao={situacao}
        controles={controles}
        troca={troca}
        trocaAberta={trocando && troca !== null}
        aviso={
          !sessao.aoVivo ? t("partidaOnline.desconectado") : recusa ? recusa : trocas.length > 0 && pausa ? t("partidaOnline.trocaEnviada") : undefined
        }
        acaoDoFim={
          <div className="pf-online-fim">
            {fim && fim.factors.length > 0 && (
              <div className="pf-online-fatores">
                {fim.factors.map((fator) => (
                  <div key={fator.key} className="row" style={{ gap: 8 }}>
                    <Bar value={Math.round(fator.impact * 100)} />
                    <span style={{ fontSize: 12 }}>{fatorDaPartida(fator.key, fator.params)}</span>
                  </div>
                ))}
              </div>
            )}
            <button type="button" className="primary" onClick={aoFechar}>
              {t("partidaOnline.fechar")}
            </button>
          </div>
        }
      />
    </div>
  );
}

/** Um lado da partida como o campo desenha, a partir do começo transmitido. */
function ladoDaPartida(
  clube: ClubeDaVisao,
  side: MatchSidePayload | null,
): LadoDaPartida & { todos: Map<string, LadoDaPartida["emCampo"][number]["jogador"]> } {
  const todos = new Map<string, LadoDaPartida["emCampo"][number]["jogador"]>();
  for (const jogador of side?.players ?? []) {
    todos.set(jogador.id, { id: jogador.id, name: jogador.name, shirt: jogador.shirt, pos: jogador.pos, nationality: jogador.nationality });
  }
  const slots = side?.slots ?? [];
  const titulares = (side?.players ?? []).slice(0, slots.length);
  return {
    clubId: clube.id,
    name: clube.name,
    short: clube.short,
    colors: clube.colors,
    formation: side?.formation ?? "",
    slots,
    custom: side?.custom ?? null,
    emCampo: titulares.map((jogador) => ({
      jogador: { id: jogador.id, name: jogador.name, shirt: jogador.shirt, pos: jogador.pos, nationality: jogador.nationality },
      sentOff: false,
    })),
    todos,
  };
}
